/**
 * OneSignal helper — links the signed-in NexusTrac user to their OneSignal
 * identity (External ID = user_id) and registers the browser push
 * subscription with our backend so targeted push notifications work.
 *
 * The OneSignal v16 SDK is loaded globally from `public/index.html` via the
 * `OneSignalDeferred` pattern. On the preview origin OneSignal init fails
 * (domain check), so every helper here is wrapped in try/catch and a
 * `window.OneSignal` presence guard — calling any of these on preview is a
 * no-op.
 */
import { BACKEND_URL } from '../config/canonical';

const defer = (fn) => {
  if (typeof window === 'undefined') return;
  window.OneSignalDeferred = window.OneSignalDeferred || [];
  window.OneSignalDeferred.push(fn);
};

/** Link this user to OneSignal + register the push subscription in our DB. */
export const linkOneSignalUser = (user, token) => {
  if (!user?.id || !token) return;
  defer(async (OneSignal) => {
    try {
      // 1. Tell OneSignal who this is (External ID = NexusTrac user_id).
      //    Calling this repeatedly with the same id is a no-op.
      if (OneSignal?.login) {
        await OneSignal.login(String(user.id));
      }
      // 2. Add the email alias so OneSignal's email channel can find them.
      const email = user.email || user.primary_email;
      if (email && OneSignal?.User?.addEmail) {
        try { await OneSignal.User.addEmail(email); } catch { /* already added */ }
      }
      // 3. Once a browser push subscription exists, ship its id to backend
      //    so server-side pushes resolve to this user.
      const sub = OneSignal?.User?.PushSubscription;
      const subId = sub?.id;
      if (subId) {
        await sendSubscriptionToBackend(subId, token);
      } else if (sub?.addEventListener) {
        sub.addEventListener('change', async (evt) => {
          const newId = evt?.current?.id || OneSignal?.User?.PushSubscription?.id;
          if (newId) {
            await sendSubscriptionToBackend(newId, token);
          }
        });
      }
    } catch (err) {
      // Preview origin or OneSignal blocked — safe to ignore.
      // eslint-disable-next-line no-console
      console.debug('[OneSignal] link skipped:', err?.message || err);
    }
  });
};

const sendSubscriptionToBackend = async (subscriptionId, token) => {
  try {
    await fetch(`${BACKEND_URL}/api/users/me/push-subscription`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ subscription_id: subscriptionId }),
    });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.debug('[OneSignal] backend register failed:', err?.message || err);
  }
};

export const unlinkOneSignalUser = () => {
  defer(async (OneSignal) => {
    try {
      if (OneSignal?.logout) await OneSignal.logout();
    } catch { /* ignore */ }
  });
};
